import {Component, Inject, LOCALE_ID, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {CustomerLockingReasonType} from '~core/enum/customner-locking-reason.enum';
import {CustomerOverViewService} from './customer-overview.service';
import {LocalModalComponent} from '~shared/components/elements/local-modal/local-modal.component';
import {ModalService} from '~shared/components/elements/global-modal/modal.service';
import {ModulesType, NotificationType} from '~core/enum';
import {TooltipTranslationService} from '~shared/translations';
import {
  ActivityHistoryService, AttributeService, CustomerService, DefaultNotificationService,
  ExportService, NavigationService, ScreenSizeService, StorageService, TixxConnectionService,
} from '~core/services';
import {Activity, AuthenticatedUser, Club, Customer, CustomerScore, SidebarOptions} from '~core/models';
import {SidebarService} from '~core/components/sidebar/sidebar.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {CustomerDataComponent} from "~features/customer-overview/components/forms/customer-data/customer-data.component";
import {CommunicationDataComponent} from "~features/customer-overview/components/forms/communication-data/communication-data.component";
import {CommunicationDataInfoComponent} from "~features/customer-overview/components/info-tiles/communication-data-info/communication-data-info.component";
import {AttributesComponent} from "~features/customer-overview/components/forms/attributes/attributes.component";
import {ActivityHistoryComponent} from "~features/customer-overview/components/info-tiles/activity-history/activity-history.component";
import {AddressListComponent} from "~features/customer-overview/components/info-tiles/address-list/address-list.component";
import {BankDataListComponent} from "~features/customer-overview/components/info-tiles/bank-data-list/bank-data-list.component";
import {CrmMembershipListComponent} from "~features/customer-overview/components/info-tiles/membership-list/crm-membership/crm-membership-list.component";
import {ContactManagementComponent} from "~features/customer-overview/components/forms/contact-management/contact-management.component";

/**
 * Handles contact management.
 */
@UntilDestroy()
@Component({
  templateUrl: 'customer-overview.component.html',
  styleUrls: ['./customer-overview.component.scss']
})
export class CustomerOverviewComponent implements OnInit, OnDestroy {
  public customer: Customer = null;
  public selectedClub: Club;
  public authenticatedUser: AuthenticatedUser;
  public customerInfo: { text: 'customer info' };
  public inputMaxCharLength: number = 255;
  public communicationDataSpinner: string = 'subscriptionStatusSpinner';
  public customerScore: CustomerScore;
  public i18nFanScore: any = $localize`:@@general.status.fanScore:Fan Score`;
  public i18nLast12Months: any;
  public scoreLoading: boolean = false;
  public updateCustomerLockedForm: UntypedFormGroup;
  public updateCustomerUnlockedForm: UntypedFormGroup;
  public updateDuplicateEmailForm: UntypedFormGroup;
  public lockingReasons: Array<any> = [];
  public isEmailValid: boolean = false;

  public listOfPanels: any = {
    address: true,
    bankData: true,
    membership: true,
    lastContacts: true,
    reminders: true,
    merchandising: true,
    ticketing: true,
    tixxSelectSubmissions: true,
    campaignActivity: true,
    history: false,
  };

  @ViewChild(CustomerDataComponent) public customerDataComponent: CustomerDataComponent;
  @ViewChild(CommunicationDataComponent) public communicationDataComponent: CommunicationDataComponent;
  @ViewChild(CommunicationDataInfoComponent) public communicationDataInfoComponent: CommunicationDataInfoComponent;
  @ViewChild(AttributesComponent) public attributesComponent: AttributesComponent;
  @ViewChild(ActivityHistoryComponent) public activityHistoryComponent: ActivityHistoryComponent;
  @ViewChild(AddressListComponent) public customerAddressListComponent: AddressListComponent;
  @ViewChild(BankDataListComponent) public bankDataListComponent: BankDataListComponent;
  @ViewChild(CrmMembershipListComponent) public crmMembershipListComponent: CrmMembershipListComponent;
  @ViewChild(ContactManagementComponent) public contactManagementComponent: ContactManagementComponent;
  @ViewChild('customerScoreModal') customerScoreModal: LocalModalComponent;
  @ViewChild('lockCustomerModal') lockCustomerModal: LocalModalComponent;
  @ViewChild('duplicateEmailModal') duplicateEmailModal: LocalModalComponent;

  public i18nGdprDisclosure: any = $localize`:@@general.tooltip.gdprDisclosure:GDPR Disclosure`;
  public i18nExportModalTitle: any = $localize`:@@general.title.exportModal:Download GDPR relevant data?`;
  public i18nExportModalParagraph: any = $localize`:@@general.paragraph.exportModal:In case a customer requested his GDPR relevant data there is a timeframe of one month in order to provide those. In order to provide all required information this download needs to be executed in all systems of the EVENTIM Sports product suite. Do you want to download the information now?`;
  public i18nUnlockCustomerTitle: any = $localize`:@@general.title.unlockCustomer:Unlock customer?`;
  public i18nLockCustomerTitle: any = $localize`:@@general.title.lockCustomer:Lock customer?`;
  public i18nDuplicateEmailTitle: any = $localize`:@@general.title.enterNewEmail:Enter a new email address`;
  public i18nUnlockCustomerParagraph: any = $localize`:@@general.paragraph.unlockCustomer:Would you like to unlock this customer? This will also affect connected systems.`;

  constructor(
    private customerService: CustomerService,
    private activityHistoryService: ActivityHistoryService,
    private screenSizeService: ScreenSizeService,
    public sidebarService: SidebarService,
    public customerOverviewService: CustomerOverViewService,
    public attributeService: AttributeService,
    public tixxConnectionService: TixxConnectionService,
    public navigationService: NavigationService,
    public storageService: StorageService,
    public spinnerService: SpinnerService,
    public buttonsTranslations: ButtonsTranslations,
    @Inject(LOCALE_ID) public locale: string,
    public tooltipTranslations: TooltipTranslationService,
    public notificationService: DefaultNotificationService,
    private exportService: ExportService,
    private modalService: ModalService,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    public flTranslations: FormLabelTranslationService,
  ) {
    this.navigationService.setCurrentBreadcrumbs(['crm', 'customerProfile']);
    this.navigationService.setCurrentPage('customerOverview');
    this.customerService.castSelectedCustomer.pipe(untilDestroyed(this)).subscribe(customer => this.customer = customer);
    this.selectedClub = this.storageService.selectedClubValue;
    this.authenticatedUser = this.storageService.authenticatedUserValue;

    if (this.screenSizeService.screenSize === 'desktop') {
      this.sidebarService.setSidebar(new SidebarOptions(true, true));
    } else {
      this.sidebarService.setSidebar(new SidebarOptions(false, true));
      this.setAllPanels(false);
    }
  }

  /**
   * Create the form and fetch the current customer
   */
  public ngOnInit(): any {
    const timeframeInMonths = this.selectedClub.modules?.find(el => el.name === ModulesType.CUSTOMER_SCORE)['customerScoreConfiguration'].timeframeInMonths;
    this.i18nLast12Months = $localize`:@@general.status.last12Months:(last ${timeframeInMonths} months)`;
    this.activateActivityHistory(this.customerInfo);
    if (!this.customer.hasEmail && !this.customer.isDuplicate) {
      const noEmailWarningTranslatedString = $localize`:@@customerOverview.notification.customerEmailWarning:This customer currently does not have an email address.`;

      this.notificationService.showNotification({
        type: NotificationType.WARNING,
        message: noEmailWarningTranslatedString
      });
    } else if (!this.customer.hasTixxId) {
      const noTixxIdWarningTranslatedString = $localize`:@@customerOverview.notification.customerTixxIdWarning:Currently this customer can not be edited as it has no Tixx customer number assigned yet.`;

      this.notificationService.showNotification({
        type: NotificationType.WARNING,
        message: noTixxIdWarningTranslatedString
      });
    }

    setTimeout(() => {
      if (this.selectedClub.isCustomerScoreActive) {
        this.scoreLoading = true;
        this.customerService.getCustomerScore(this.customer.id, this.selectedClub.preferredCurrency).pipe(untilDestroyed(this))
          .subscribe({
            next: res => {
              this.customerScore = res;
              this.scoreLoading = false;
            },
            error: (error: HttpErrorResponse) => {
              this.scoreLoading = false;
            }
          });
      }
    }, 100);

    this.setCustomerStateForm();
  }

  /**
   * Close all modals on destroy.
   */
  public ngOnDestroy(): any {
    this.customerOverviewService.toggleAllModal(false);
    this.attributeService.resetAttributes();
  }

  /**
   * Announce / notify change with activity history service.
   */
  public activateActivityHistory(activity: Activity): void {
    this.activityHistoryService.announce(activity);
    this.customerOverviewService.toggleAllModal(false);
  }


  /**
   * After pressing delete in the delete modal, this function decides which API endpoint to use
   */
  public deleteData(dataId: number, type: string): any {
    switch (type) {
      case 'address':
        this.customerAddressListComponent.deleteAddress(dataId);
        break;
      case 'bank data':
        this.bankDataListComponent.deleteBankData(dataId);
        break;
      case 'membership':
        this.crmMembershipListComponent.deleteMembershipData(dataId);
        break;
      default:
    }
  }

  /**
   * Scroll function
   */
  public scroll(el?: HTMLElement): void {
    const scrollTimeout = 300;
    setTimeout(() => {
      el.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, scrollTimeout);
  }

  /**
   * Opens or closes all panels
   */
  public setAllPanels(status: boolean): void {
    Object.keys(this.listOfPanels).forEach(panel => {
      this.setPanel(panel, status);
    });

  }

  /**
   * Opens or closes all panels
   */
  public setPanel(panel: string, status: boolean): void {
    this.listOfPanels[panel] = status;
  }

  /**
   * Triggers the service to call API for activation of this customer to be the primary
   */
  activateCustomer(): void {
    this.spinnerService.show('screenSpinner');
    this.customerService.markAsPrimary(this.customer.id)
      .subscribe({
        next: customer => {
          location.reload();
        },
        error: (error: Error) => {
          location.reload();
        }
      });
  }

  public async showExportRequestModal(): Promise<any> {
    this.modalService.setModalOptions(this.i18nExportModalTitle, this.i18nExportModalParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      this.makeExportRequest();
    }
  }

  public async showUnlockingCustomerModal(): Promise<any> {
    this.modalService.setModalOptions(this.i18nUnlockCustomerTitle, this.i18nUnlockCustomerParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      this.updateFormSubmit(this.updateCustomerUnlockedForm);
    }
  }

  /**
   * Make export request
   */
  makeExportRequest(): void {
    const request = {
      club: `/api/clubs/${this.selectedClub.id}`,
      type: 'gdpr',
      resourceId: this.customer.id.toString() // Must be sent as a string
    };
    this.exportService.createExportRequest(request)
      .subscribe({
        next: (res: any) => {
          this.notificationService.showNotification({
            type: NotificationType.INFO,
            message: this.exportService.i18nExportInitiated,
          });
          this.modalService.setStatus(false);

          const interval = setInterval(() => {
            this.exportService.getExportRequestById(res.id)
              .subscribe({
                next: (res2: any) => {
                  if (res2.media.contentUrl !== null) {
                    clearInterval(interval);
                    this.notificationService.showNotification({
                      type: NotificationType.SUCCESS,
                      message: `${this.exportService.i18nExportReady} <a class="esf-button esf-button--simple"
              href="${res2.media.contentUrl}" target="_blank">${this.exportService.i18nClickHereToDownload}</a>`,
                      duration: undefined,
                    });
                  }
                },
                error: (error: Error) => {
                  clearInterval(interval);
                  this.modalService.setStatus(false);
                }
              });
          }, 3000);

        },
        error: (error: Error) => {
          this.modalService.setStatus(false);
        }
      });
  }

  public mergeCustomer(): void {
    this.customerService.searchDuplicates(this.customer.id)
      .pipe(untilDestroyed(this))
      .subscribe(data => {
        this.router.navigate(['/club', this.selectedClub.id, 'merge-customer'], {
          queryParams: {firstCustomer: this.customer.id, secondCustomer: data[0].id},
          state: {fromLocation: 'customerOverview'}
        });
      });
  }

  get customerScoreName(): string {
    let name = '';
    if (this.customer.firstName) {
      name += `${this.customer.firstName} `;
    }
    if (this.customer.lastName) {
      name += `${this.customer.lastName}`;
    }
    return `${name} ${this.i18nFanScore} ${this.i18nLast12Months}`;
  }

  setCustomerStateForm() {
    this.lockingReasons.push(
      {value: CustomerLockingReasonType.DEPARTED, status: this.flTranslations.i18nDeparted},
      {value: CustomerLockingReasonType.SUSPICION_OF_FRAUD, status: this.flTranslations.i18nSuspicionOfFraud},
      {value: CustomerLockingReasonType.ENCASHMENT, status: this.flTranslations.i18nEncashment},
      {value: CustomerLockingReasonType.BLACKLIST, status: this.flTranslations.i18nBlacklist},
      {value: CustomerLockingReasonType.STADIUM_BAN, status: this.flTranslations.i18nStadiumBan},
      {value: CustomerLockingReasonType.OTHER_REASON, status: this.flTranslations.i18nOtherReason},
      {value: CustomerLockingReasonType.CUSTOMER_REQUEST, status: this.flTranslations.i18nCustomerRequest},
      {value: CustomerLockingReasonType.CHARGE_BACK, status: this.flTranslations.i18nChargeBack},
      {value: CustomerLockingReasonType.DIRECT_DEBIT_RECALL, status: this.flTranslations.i18nDirectDebitRecall},
    );

    this.updateCustomerLockedForm = this.formBuilder.group({
      state: this.formBuilder.group({
        status: ['locked'],
        lockingReason: this.formBuilder.group({
          type: [null, [Validators.required]],
        })
      }),
    });

    this.updateCustomerUnlockedForm = this.formBuilder.group({
      state: this.formBuilder.group({
        status: ['active']
      }),
    });

    this.updateDuplicateEmailForm = this.formBuilder.group({
      email: ['']
    });
  }

  updateFormSubmit(form) {
    this.lockCustomerModal.setSpinner(true);
    if (form.valid) {
      this.customerService.updateCustomerState(form.value, this.customer.id).pipe(untilDestroyed(this))
        .subscribe({
          next: newCustomer => {
            this.customerService.setSelectedCustomer(newCustomer);
            this.modalService.setStatus(false);
            this.lockCustomerModal.setSpinner(false);
            this.lockCustomerModal.setModalStatus(false);
          },
          error: (error: HttpErrorResponse) => {
            this.lockCustomerModal.setSpinner(false);

          }
        });
    }
  }

  duplicateEmailFormSubmit(form) {
    this.duplicateEmailModal.setSpinner(true);
    this.customerService.updateCustomerEmail(form.value, this.customer.id).pipe(untilDestroyed(this))
      .subscribe({
        next: newCustomer => {
          console.log(newCustomer);
          this.customerService.setSelectedCustomer(newCustomer);
          this.modalService.setStatus(false);
          this.duplicateEmailModal.setSpinner(false);
          this.duplicateEmailModal.setModalStatus(false);
        },
        error: (error: HttpErrorResponse) => {
          this.duplicateEmailModal.setSpinner(false);

        }
      });
  }

}
